<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, useAttrs, type ComponentPublicInstance } from 'vue';
import InputTextBase from './InputTextBase.vue';
import { useValidator } from '@/composeables/validator';
import { ValidatorMessage, type IInputNullableIntModelValueType, type IInputNullableIntPropsWithoutModelValue } from '@/components';
import type { IEditorComponent } from '@/objects/gui';
import { isCommonKey } from '@/utilities/keyboardutils';
import type { IEmitBlur, IEmitFocus } from '../Interfaces';

const props = withDefaults(defineProps<IInputNullableIntPropsWithoutModelValue>(), {
    emptyValue: null,
    validator: null,

    handleZeroDigitAsNull: false,
});

const [modelValue, modelValueModifiers] = defineModel<IInputNullableIntModelValueType, 'lazy'>({ required: true });

// The emits available
const emits = defineEmits<
    {
        /**
         * Raised when the value changes
         */
        (e: 'update:modelValue', value: string): void;

        /**
         * Raised when the pattern does not match
         */
        (e: 'patternMismatch', event: Event): void;

        /**
         * When the value has changed
         */
        (e: 'change', newvalue: number | null, oldvalue: number | null): void;

        /**
         * Whenever the input is clicked
         */
        (e: 'click', ev: MouseEvent): void;

        (e: 'keydown', ev: KeyboardEvent): void;
    } & IEmitFocus &
        IEmitBlur
>();

const { Validate, ValidateMessage } = useValidator(props.validator, modelValue.value);

/**
 * Allow only numbers and delete or CTRL + key combinations
 *
 * @param event
 */
const allowOnlyNumbersAndDelete = (event: KeyboardEvent) => {
    emits('keydown', event);

    if (isCommonKey(event)) return;

    if (event == undefined || event.key == undefined) {
        return;
    }

    // Allow only numbers
    if (event.key != '-' && (event.key.length != 1 || isNaN(parseInt(event.key)))) {
        event.preventDefault();
    }
};

let parsedValue: number | null = null;

const textValue = computed<string>({
    get() {
        if (props.handleZeroDigitAsNull && modelValue.value === 0) {
            return props.emptyValue?.toString() || '';
        }
        return (modelValue.value ?? props.emptyValue)?.toString() || '';
    },
    set(value) {
        if (!Validate(value)) {
            return;
        }

        if (props.handleZeroDigitAsNull && value === '0') {
            value = props.emptyValue?.toString() || '';
        }

        parsedValue = parseInt(value);

        if (modelValueModifiers.lazy) {
            return;
        }

        if (isNaN(parsedValue)) {
            modelValue.value = props.emptyValue;
            return;
        }

        modelValue.value = parsedValue;
    },
});

const mismatch = async () => {
    // If the modelValue is already the emptyValue, we dont change it and then we dont emit anything, and the text in the box doesnt change.
    // So if the modelValue is already the emptyValue, we change it to -999 and then back to the emptyValue so the UI updated.
    // This is not nice. I also hasent been tested since we changed from prop+emit to defineModel, so maybe it's not needed anymore.
    if (modelValue.value == props.emptyValue) {
        modelValue.value = -999;
        await nextTick();
    }
    modelValue.value = props.emptyValue;
};

// see mismatch comment, this will give us a warning in console
if (props.emptyValue == -999) {
    console.warn('InputInt: emptyValue should not be -999, it will not update gui on pattern mismatch');
}

const inputref = ref<ComponentPublicInstance<IEditorComponent> | null>(null);

// If the input has had focus. We use this to show the validation message
const hasHadFocus = ref(false);

const onBlur = (ev: Event) => {
    hasHadFocus.value = true;

    // if the value is changed and we are using lazy model modifier, we might need to trigger an update manually
    if (modelValueModifiers.lazy) {
        if (parsedValue !== null && parsedValue != modelValue.value) {
            if (isNaN(parsedValue)) {
                modelValue.value = props.emptyValue;
            } else {
                modelValue.value = parsedValue;
            }
        }
    }

    emits('blur', ev);
};

const onChanged = (newvalue: string | null, oldvalue: string | null) => {
    // We try and parse the newvalue to number
    let newNumValue: number | null = null;
    let oldNumValue: number | null = null;
    if (newvalue !== null) {
        const parsedNew = parseInt(newvalue);
        newNumValue = isNaN(parsedNew) ? null : parsedNew;
    }
    if (oldvalue !== null) {
        const parsedOld = parseInt(oldvalue);
        oldNumValue = isNaN(parsedOld) ? null : parsedOld;
    }

    emits('change', newNumValue, oldNumValue);
};

defineExpose<IEditorComponent>({
    focus: () => inputref.value?.focus(),
    validateMessage: ValidateMessage,
    KeyboardEvent: (ev: KeyboardEvent) => {
        const input = ev.key;
        if (!isNaN(Number(input))) {
            textValue.value = input;
            return;
        }
        modelValue.value = null;
    },
});
</script>

<template>
    <ValidatorMessage
        :message="ValidateMessage"
        :hide="!hasHadFocus"
    >
        <div class="form-control-number input-group flex-nowrap">
            <InputTextBase
                ref="inputref"
                v-model="textValue"
                class="text-end"
                :class="$attrs.class"
                :pattern="'[\\-0-9]*'"
                :placeholder="placeholder"
                :select-on-focus="true"
                @pattern-mismatch="mismatch"
                @keydown.capture="allowOnlyNumbersAndDelete"
                :maxlength="maxlength"
                :autofocus="props.autofocus"
                :readonly="props.readonly"
                :disabled="props.disabled"
                :icon="props.icon ?? 'fa-hashtag'"
                :icon-family="props.iconFamily"
                :id="props.id"
                :label-position="props.labelPosition"
                :label="props.label"
                :mark-text-part="props.markTextPart"
                :modelModifiers="modelModifiers"
                :tooltip="props.tooltip"
                :posttext="props.posttext"
                :border="props.border"
                :autocomplete="props.autocomplete"
                type="number"
                @blur="onBlur"
                @changed="onChanged"
            />
        </div>
    </ValidatorMessage>
</template>
